"use client";

import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/classnames";
import { __ } from "@/lib/i18n/client.functions";
import { Locales } from "@/lib/i18n";
import React, { useEffect, useState } from "react";
import { getCartQuantityStep, toLotQuantity, toPieceQuantity } from "@/lib/product-units";

interface QuantitySelectorProps {
  onQuantityChange?: (newQuantity: number) => void;
  className?: string;
  variant?: "default" | "secondary";
  count: number;
  setCount: React.Dispatch<React.SetStateAction<number>>;
  locale: Locales;
  disabled?: boolean;
  step?: number;
}

const QuantitySelector = ({
  count,
  setCount,
  onQuantityChange,
  className,
  variant = "default",
  locale,
  disabled,
  step = 1,
}: QuantitySelectorProps) => {
  const quantityStep = getCartQuantityStep(step);
  const [inputValue, setInputValue] = useState(toLotQuantity(count, quantityStep).toString());
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setInputValue(toLotQuantity(count, quantityStep).toString());
    }
  }, [count, isEditing, quantityStep]);

  const handleDecrease = () => {
    setCount((prevCount) => {
      const newCount = prevCount > quantityStep ? prevCount - quantityStep : prevCount;
      if (onQuantityChange) onQuantityChange(newCount);
      return newCount;
    });
  };

  const handleIncrease = () => {
    setCount((prevCount) => {
      const newCount = prevCount + quantityStep;
      if (onQuantityChange) onQuantityChange(newCount);
      return newCount;
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setInputValue(value);
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const numValue = parseInt(inputValue) || 1;
    const clampedValue = toPieceQuantity(numValue, quantityStep);

    setCount(clampedValue);
    setInputValue(toLotQuantity(clampedValue, quantityStep).toString());
    if (onQuantityChange) onQuantityChange(clampedValue);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setInputValue(toLotQuantity(count, quantityStep).toString());
      setIsEditing(false);
      e.currentTarget.blur();
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    setTimeout(() => {
      const input = document.activeElement as HTMLInputElement;
      if (input) input.select();
    }, 0);
  };

  return (
    <div
      role="group"
      aria-labelledby="quantity-label"
      className={cn(
        "bg-product-card flex h-10 w-[150px] flex-row items-center justify-between rounded-md border border-black/20 md:h-10",
        variant === "secondary" && "p-2",
        className
      )}
    >
      <span id="quantity-label" className="sr-only">
        {__("Quantity selector", locale)}
      </span>

      <button
        type="button"
        onClick={handleDecrease}
        aria-label={__("Decrease Quantity", locale)}
        disabled={count <= quantityStep || disabled}
        className={cn(
          "border-primary-light/23 flex h-full cursor-pointer items-center justify-end px-2 transition-all duration-300 ease-in-out hover:scale-105 hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-50",
          variant === "secondary" && "size-6 justify-center rounded-full border"
        )}
      >
        <Minus
          className={cn("text-primary-light size-3 shrink-0", variant === "secondary" && "text-dark-primary size-3.5")}
        />
      </button>

      <span
        aria-live="polite"
        aria-atomic="true"
        className="min-w-[5ch] text-center text-xs font-medium tabular-nums md:text-base"
      >
        <input
          size={1}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          onFocus={handleInputFocus}
          aria-live="polite"
          disabled={disabled}
          aria-atomic="true"
          aria-label={__("Quantity", locale)}
          className="w-full min-w-[5ch] border-none bg-transparent text-center font-semibold outline-none focus:ring-0 md:text-base"
          min={1}
        />
      </span>

      <button
        type="button"
        onClick={handleIncrease}
        disabled={disabled}
        aria-label={__("Increase Quantity", locale)}
        className={cn(
          "border-primary-light/23 flex h-full cursor-pointer items-center justify-end px-2 transition-all duration-300 ease-in-out hover:scale-105 hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-50",
          variant === "secondary" && "size-6 justify-center rounded-full border"
        )}
      >
        <Plus
          className={cn("text-primary-light size-3 shrink-0", variant === "secondary" && "text-dark-primary size-3.5")}
        />
      </button>
    </div>
  );
};

export default QuantitySelector;
